import { memo, useContext, useCallback, useRef } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { pushWithCruiseFilters } from 'actions/CruiseActions'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Pane from 'components/Common/Pane'
import TextButton from 'LuxKit/Button/TextButton'
import CruiseSearchInputs from './Inputs/CruiseSearchInputs'
import useQueryParams from 'hooks/useQueryParams'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'

import { trackEvent } from 'analytics/analytics'
import { getCruiseSearchFiltersBar } from 'lib/cruises/cruiseUtils'
import useCruiseInternationalMarket from 'hooks/useCruiseInternationalMarket'

const CruiseSearchFiltersBarGrid = styled.div<{ $hideCruiseLine?: boolean }>`
  display: grid;
  gap: ${rem(12)};
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template: ${({ $hideCruiseLine }) => $hideCruiseLine
    ? `"destination-input departure-input date-input" auto / 1fr 1fr 1fr ${rem(112)}`
    : `"destination-input departure-input cruise-line-input date-input" auto / 1fr 1fr 1fr 1fr ${rem(112)}`};
  }
`

const SearchButton = styled(TextButton)`
  ${mediaQueryUp.tablet} {
    height: ${rem(50)};
  }
`

interface Props {
  forceSearchPage?: boolean
  hideCruiseLine?: boolean
}

function CruiseSearchFiltersBar({ forceSearchPage, hideCruiseLine }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const isInternationalMarket = useCruiseInternationalMarket()
  const shouldHideCruiseLine = hideCruiseLine || isInternationalMarket
  const globalSearch = useContext(GlobalSearchStateContext)
  const pathname = useAppSelector(state => state.router.location.pathname)
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const queryParams = useQueryParams()
  const searchBarRef = useRef<HTMLDivElement>(null)
  const {
    searchItems: globalSearchItems,
    secondarySearchItems: globalSecondarySearchItems,
    flexibleMonths: globalFlexibleMonths,
    durationMin: globalDurationMin,
    durationMax: globalDurationMax,
    durationRange: globalDurationRange,
    checkinDate: globalCheckinDate,
    checkoutDate: globalCheckoutDate,
    cruiseLines: globalCruiseLines,
  } = globalSearch

  // update url when clicking search button
  const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: mapGlobalSearchContextToSnowplowSearchEvent({
        searchItems: globalSearchItems,
        secondarySearchItems: globalSecondarySearchItems,
        searchVerticals: new Set(['cruises']),
        flexibleMonths: globalFlexibleMonths,
        durationMin: globalDurationMin,
        durationMax: globalDurationMax,
        durationRange: globalDurationRange,
        checkinDate: globalCheckinDate,
        checkoutDate: globalCheckoutDate,
        cruiseLines: globalCruiseLines,
      }),
    }))

    dispatch(pushWithCruiseFilters('/search/cruises', globalSearch, queryParams))
  }, [dispatch, globalSearch, queryParams, globalSearchItems, globalSecondarySearchItems, globalFlexibleMonths, globalDurationMin, globalDurationMax, globalDurationRange, globalCheckinDate, globalCheckoutDate, globalCruiseLines])

  const onChange = useCallback((values: App.CruiseGlobalFilters) => {
    const { pageName, filters } = getCruiseSearchFiltersBar(values, globalSearch, pathname, regionCode, forceSearchPage)
    dispatch(pushWithCruiseFilters(pageName, filters, queryParams))
  }, [globalSearch, dispatch, pathname, regionCode, queryParams, forceSearchPage])

  const searchButtonText = intl.formatMessage({
    id: 'yizE-ctvGk4M6jfyylaI',
    defaultMessage: 'Search',
    description: 'Search button text for cruise search in the cruise offer or booking section',
  })

  return <Pane ref={searchBarRef}>
    <CruiseSearchFiltersBarGrid
      as="form"
      onSubmit={onSubmit}
      $hideCruiseLine={shouldHideCruiseLine}
    >
      <CruiseSearchInputs
        onChange={onChange}
        dropdownAnchorRef={searchBarRef}
        hideCruiseLine={shouldHideCruiseLine}
      />

      <SearchButton
        kind="primary"
        type="submit"
        size="large"
      >
        {searchButtonText}
      </SearchButton>
    </CruiseSearchFiltersBarGrid>
  </Pane>
}

export default memo(CruiseSearchFiltersBar)
